import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import './OrderMessages.css';

const OrderMessages = ({ order, onClose }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showError } = useNotification();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (order) {
      fetchMessages();
      // Poll for new messages every 5 seconds
      const interval = setInterval(fetchMessages, 5000);
      
      // Prevent body scroll when modal is open on mobile
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalWidth = document.body.style.width;
      document.body.style.overflow = 'hidden';
      // On mobile, also prevent scrolling by fixing body position
      if (window.innerWidth <= 768) {
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
      }
      
      return () => {
        clearInterval(interval);
        // Restore body scroll when modal closes
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.width = originalWidth;
      };
    }
  }, [order]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [newMessage]);

  // Handle mobile keyboard visibility
  useEffect(() => {
    const handleResize = () => {
      // Scroll to bottom when viewport changes (keyboard appears/disappears)
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    };

    // Listen for viewport changes (keyboard events)
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    // Also listen for visual viewport changes (better for mobile keyboards)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      // Use block: 'end' to ensure it scrolls to the bottom
      messagesEndRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest'
      });
    }
  };

  const fetchMessages = async () => {
    if (!order?._id) return;
    try {
      const response = await axios.get(`${API_URL}/messages/order/${order._id}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending || !order?._id) return;

    setSending(true);
    try {
      const response = await axios.post(
        `${API_URL}/messages/order/${order._id}`,
        { message: newMessage.trim() }
      );
      setMessages([...messages, response.data]);
      setNewMessage('');
      // Reset textarea height after sending
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      // Scroll to bottom after sending
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    } catch (error) {
      console.error('Error sending message:', error);
      showError(t('orders.messageFailed'));
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return t('chatPicker.justNow');
    if (minutes < 60) return t('chatPicker.minutesAgo', { count: minutes });
    if (hours < 24) return t('chatPicker.hoursAgo', { count: hours });
    if (days < 7) return t('chatPicker.daysAgo', { count: days });
    return date.toLocaleDateString();
  };

  const getRoleLabel = (role) => {
    const keyMap = {
      main_admin: 'orders.roleAdmin',
      restaurant_owner: 'orders.roleRestaurantOwner',
      store_owner: 'orders.roleStoreOwner',
      delivery_driver: 'orders.roleDeliveryDriver',
      customer: 'orders.roleCustomer'
    };
    const key = keyMap[role];
    return key ? t(key) : role;
  };

  // Translated order status label for chat status log (respects customer language)
  const getStatusLabel = (status) => {
    if (!status) return t('orders.statusLog.noPreviousStatus');
    const key = `activeOrder.status.${status}`;
    const translated = t(key);
    return translated !== key ? translated : status;
  };

  // Status log message in customer's language (uses statusChange when available, else known message keys)
  const getStatusLogMessage = (message) => {
    const sc = message.statusChange;
    if (sc && (sc.oldStatus != null || sc.newStatus != null)) {
      if (sc.oldStatus == null && sc.newStatus === 'pending') {
        return t('orders.statusLog.orderCreated');
      }
      const from = getStatusLabel(sc.oldStatus);
      const to = getStatusLabel(sc.newStatus);
      return t('orders.statusLog.changedFromTo', { from, to });
    }
    const text = (message.message || '').trim();
    if (text === 'Driver accepted the order') return t('orders.statusLog.driverAccepted');
    if (text === 'Delivery driver accepted the delivery') return t('orders.statusLog.driverAcceptedDelivery');
    if (text === 'Delivery driver declined the delivery') return t('orders.statusLog.driverDeclined');
    return message.message;
  };

  if (!order) return null;

  return (
    <div className="order-messages-overlay" onClick={(e) => {
      // Only close if clicking directly on overlay, not on modal
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="order-messages-modal" onClick={(e) => e.stopPropagation()}>
        <div className="order-messages-header">
          <div>
            <h3>Order #{order.orderNumber || order._id.slice(-6)}</h3>
            <p>{order.restaurantName}</p>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="messages-list" ref={messagesContainerRef}>
          {loading ? (
            <div className="loading-messages">{t('orders.loadingMessages')}</div>
          ) : messages.length === 0 ? (
            <div className="no-messages">{t('orders.noMessages')} {t('orders.startConversation')}</div>
          ) : (
            messages.map((message, index) => (
              <div
                key={message._id || index}
                className={`message-item ${message.type === 'status_log' ? 'status-log' : ''} ${
                  message.sender?._id === user?._id ? 'sent' : 'received'
                }`}
              >
                {message.type === 'status_log' ? (
                  <div className="status-log-message">
                    <i className="fas fa-info-circle"></i>
                    <span>{getStatusLogMessage(message)}</span>
                    <span className="message-time">{formatDate(message.createdAt)}</span>
                  </div>
                ) : (
                  <>
                    <div className="message-sender">
                      {getRoleLabel(message.sender?.role || 'customer')}
                      {message.sender?._id === user?._id && ` (${t('orders.you')})`}
                    </div>
                    <div className="message-content">
                      {message.message}
                    </div>
                    <div className="message-time">{formatDate(message.createdAt)}</div>
                  </>
                )}
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        <form className="message-input-form" onSubmit={handleSendMessage}>
          <textarea
            ref={textareaRef}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={t('orders.typeMessage')}
            disabled={sending}
            className="message-input"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            rows={3}
            onKeyDown={(e) => {
              // Allow sending with Ctrl+Enter or Cmd+Enter
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handleSendMessage(e);
              }
            }}
          />
          <button type="submit" disabled={sending || !newMessage.trim()} className="send-button" aria-label={t('orders.sendMessage')}>
            <i className="fas fa-paper-plane"></i>
            <span>{t('orders.sendMessage')}</span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default OrderMessages;
